import {
  Component,
  ElementRef,
  HostListener,
  ViewChild,
  computed,
  inject,
  signal,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  AuthService,
  IconComponent,
  SignoutIcon,
  UserprofileIcon,
  type IconDef,
} from '../../../../../../../libs/core';
import { PortalAuthSessionService } from '../../../pages/auth/services/portal-auth-session.service';
import { PortalDarkModeService } from '../../../core/portal-dark-mode.service';

@Component({
  selector: 'app-portal-topbar',
  standalone: true,
  imports: [IconComponent],
  templateUrl: './portal-topbar.component.html',
})
export class PortalTopbarComponent {
  @ViewChild('profileMenu', { static: false }) private profileMenuRef?: ElementRef<HTMLElement>;

  private readonly router = inject(Router);
  protected readonly authService = inject(AuthService);
  protected readonly sessionService = inject(PortalAuthSessionService);
  protected readonly darkMode = inject(PortalDarkModeService);

  protected readonly userProfileIcon: IconDef = UserprofileIcon;
  protected readonly signoutIcon: IconDef = SignoutIcon;
  protected readonly profilePopupOpen = signal(false);

  protected readonly user = computed(() => {
    const session = this.authService.session();
    const firstName = session.firstName?.trim() ?? '';
    const lastName = session.lastName?.trim() ?? '';
    const name = [firstName, lastName].filter(Boolean).join(' ') || session.email || 'Customer';
    return {
      name,
      email: session.email,
      initials: this.getUserInitials(name),
    };
  });

  protected toggleProfilePopup(event: MouseEvent): void {
    event.stopPropagation();
    this.profilePopupOpen.update((v) => !v);
  }

  protected openProfile(): void {
    this.profilePopupOpen.set(false);
    void this.router.navigate(['/profile']);
  }

  protected logout(): void {
    this.profilePopupOpen.set(false);
    this.sessionService.logout();
  }

  protected getUserInitials(name: string): string {
    const words = name.trim().split(/\s+/).filter(Boolean).slice(0, 2);
    if (words.length > 1) return `${words[0][0]}${words[1][0]}`.toUpperCase();
    return words[0]?.slice(0, 2).toUpperCase() ?? 'U';
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    const target = event.target as Node | null;
    if (!target) { this.profilePopupOpen.set(false); return; }
    const el = this.profileMenuRef?.nativeElement;
    if (this.profilePopupOpen() && (!el || !el.contains(target))) {
      this.profilePopupOpen.set(false);
    }
  }
}
